import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';

export const SIDEBAR_TOGGLE_POSITIONS = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
] as const;
export type SidebarTogglePosition = (typeof SIDEBAR_TOGGLE_POSITIONS)[number];

const RECENTLY_OPENED_MAX = 15;
export const RECENTLY_OPENED_SIDEBAR_COUNT = 5;

/** One recently opened document; stored by id and path so renames can remove stale entries. */
export interface RecentlyOpenedDocument {
  id: string;
  path: string;
}

interface AppSettings {
  isLocal: boolean;
  apiHost: string;
  localApiHost: string;
  remoteApiHost: string;
  sidebarTogglePosition: SidebarTogglePosition;
  /** Recently opened documents (id + path), most recent first. Max 15. Legacy: may be string[] (paths only). */
  recentlyOpenedDocuments?: RecentlyOpenedDocument[] | string[];
}

interface AppConfig {
  isLocal: boolean;
  localApiHost: string;
  remoteApiHost: string;
  apiHost: string;
  graphqlUrl: string;
  sidebarTogglePosition: SidebarTogglePosition;
  recentlyOpenedDocuments: RecentlyOpenedDocument[];
}

export type AppSettingsKey = keyof AppSettings;

interface AppConfigContextType {
  config: AppConfig;
  setIsLocal: (isLocal: boolean) => void;
  setLocalApiHost: (host: string) => void;
  setRemoteApiHost: (host: string) => void;
  setSidebarTogglePosition: (position: SidebarTogglePosition) => void;
  clearField: (key: AppSettingsKey) => void;
  /** Record that a document was opened; updates recently opened list (max 15). Dedupes by id. */
  recordRecentlyOpenedDocument: (id: string, path: string) => void;
  /** Remove any recent entries for this document id (e.g. after rename so stale path is gone). */
  removeRecentlyOpenedDocumentById: (id: string) => void;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(
  undefined,
);

interface AppConfigProviderProps {
  children: ReactNode;
}

const defaultSettings: AppSettings = {
  isLocal: false,
  apiHost: '',
  localApiHost: 'http://localhost:3000',
  remoteApiHost: 'https://note-plan.com',
  sidebarTogglePosition: 'top-right',
  recentlyOpenedDocuments: [],
};

export function AppConfigProvider({ children }: AppConfigProviderProps) {
  const [config, setConfig] = useState<AppConfig>(() => {
    const storedSettings = localStorage.getItem('AppSettings');
    const settings: AppSettings = storedSettings
      ? { ...defaultSettings, ...JSON.parse(storedSettings) }
      : { ...defaultSettings };

    const apiHost = settings.isLocal
      ? settings.localApiHost
      : settings.remoteApiHost;
    const graphqlUrl = `${apiHost}/api/graphql`;

    const raw = settings.recentlyOpenedDocuments;
    const recent: RecentlyOpenedDocument[] =
      raw && Array.isArray(raw)
        ? raw
            .slice(0, RECENTLY_OPENED_MAX)
            .map((item) =>
              typeof item === 'string'
                ? { id: item, path: item }
                : (item as RecentlyOpenedDocument),
            )
        : [];

    return {
      isLocal: settings.isLocal,
      localApiHost: settings.localApiHost,
      remoteApiHost: settings.remoteApiHost,
      apiHost,
      graphqlUrl,
      sidebarTogglePosition:
        settings.sidebarTogglePosition ?? defaultSettings.sidebarTogglePosition,
      recentlyOpenedDocuments: recent,
    };
  });

  const saveSettings = (newSettings: Partial<AppSettings>) => {
    const stored = localStorage.getItem('AppSettings');
    const currentSettings: AppSettings = stored
      ? { ...defaultSettings, ...JSON.parse(stored) }
      : { ...defaultSettings };
    const updatedSettings = { ...currentSettings, ...newSettings };
    localStorage.setItem('AppSettings', JSON.stringify(updatedSettings));
  };

  const recordRecentlyOpenedDocument = useCallback(
    (id: string, path: string) => {
      const trimmedPath = path?.trim();
      const trimmedId = id?.trim();
      if (!trimmedPath || !trimmedId) return;
      setConfig((prev) => {
        const list = [...(prev.recentlyOpenedDocuments ?? [])];
        const filtered = list.filter((doc) => doc.id !== trimmedId);
        filtered.unshift({ id: trimmedId, path: trimmedPath });
        const next = filtered.slice(0, RECENTLY_OPENED_MAX);
        const stored = localStorage.getItem('AppSettings');
        const current: AppSettings = stored
          ? { ...defaultSettings, ...JSON.parse(stored) }
          : { ...defaultSettings };
        localStorage.setItem(
          'AppSettings',
          JSON.stringify({ ...current, recentlyOpenedDocuments: next }),
        );
        return { ...prev, recentlyOpenedDocuments: next };
      });
    },
    [],
  );

  const removeRecentlyOpenedDocumentById = useCallback((id: string) => {
    const trimmedId = id?.trim();
    if (!trimmedId) return;
    setConfig((prev) => {
      const list = (prev.recentlyOpenedDocuments ?? []).filter(
        (doc) => doc.id !== trimmedId,
      );
      const stored = localStorage.getItem('AppSettings');
      const current: AppSettings = stored
        ? { ...defaultSettings, ...JSON.parse(stored) }
        : { ...defaultSettings };
      localStorage.setItem(
        'AppSettings',
        JSON.stringify({ ...current, recentlyOpenedDocuments: list }),
      );
      return { ...prev, recentlyOpenedDocuments: list };
    });
  }, []);

  const clearField = (key: AppSettingsKey) => {
    setConfig((prev) => {
      const next: AppConfig = {
        ...prev,
        [key]: defaultSettings[key],
      };
      if (key !== 'sidebarTogglePosition') {
        next.apiHost = next.isLocal ? next.localApiHost : next.remoteApiHost;
        next.graphqlUrl = `${next.apiHost}/api/graphql`;
      }
      return next;
    });

    const stored = localStorage.getItem('AppSettings');
    const current: AppSettings = stored
      ? { ...defaultSettings, ...JSON.parse(stored) }
      : { ...defaultSettings };
    const rest = { ...current };
    delete rest[key];
    localStorage.setItem('AppSettings', JSON.stringify(rest));
  };

  const setIsLocal = (isLocal: boolean) => {
    const apiHost = isLocal ? config.localApiHost : config.remoteApiHost;
    const graphqlUrl = `${apiHost}/api/graphql`;

    setConfig((prev) => ({
      ...prev,
      isLocal,
      apiHost,
      graphqlUrl,
    }));

    saveSettings({ isLocal });
  };

  const setLocalApiHost = (localApiHost: string) => {
    const trimmedHost = localApiHost.trim();
    const apiHost = config.isLocal ? trimmedHost : config.remoteApiHost;
    const graphqlUrl = `${apiHost}/api/graphql`;

    setConfig((prev) => ({
      ...prev,
      localApiHost: trimmedHost,
      apiHost,
      graphqlUrl,
    }));

    saveSettings({ localApiHost: trimmedHost });
  };

  const setRemoteApiHost = (remoteApiHost: string) => {
    const trimmedHost = remoteApiHost.trim();
    const apiHost = config.isLocal ? config.localApiHost : trimmedHost;
    const graphqlUrl = `${apiHost}/api/graphql`;

    setConfig((prev) => ({
      ...prev,
      remoteApiHost: trimmedHost,
      apiHost,
      graphqlUrl,
    }));

    saveSettings({ remoteApiHost: trimmedHost });
  };

  const setSidebarTogglePosition = (
    sidebarTogglePosition: SidebarTogglePosition,
  ) => {
    setConfig((prev) => ({ ...prev, sidebarTogglePosition }));
    saveSettings({ sidebarTogglePosition });
  };

  const value: AppConfigContextType = {
    config,
    setIsLocal,
    setLocalApiHost,
    setRemoteApiHost,
    setSidebarTogglePosition,
    clearField,
    recordRecentlyOpenedDocument,
    removeRecentlyOpenedDocumentById,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
}
